import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wand2, Users, Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PageHeader, Panel, Empty } from "@/components/PageHeader";
import { TierBadge } from "@/components/TierBadge";
import { api, eur, num, errMsg, fdatetime } from "@/lib/api";

const DIMS = [["r", "Recenza (giorni dall'ultimo acquisto)", "≤"], ["f", "Frequenza (n. scontrini)", "≥"], ["m", "Valore (spesa totale €)", "≥"]];

const ClusterCard = ({ c, onMembers, onCampaign, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-md" data-testid={testId}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0"><h3 className="font-heading font-semibold text-slate-900">{c.name}</h3><p className="mt-0.5 text-xs text-slate-500">{c.description}</p></div>
      <div className="text-right"><div className="font-heading text-xl font-semibold text-blue-600">{num(c.count)}</div><div className="text-[11px] text-slate-400">{(c.share || 0).toLocaleString("it-IT")}%</div></div>
    </div>
    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
      <span>Spesa media <b>{eur(c.avg_spent)}</b></span><span>Scontrini medi <b>{c.avg_tx.toLocaleString("it-IT")}</b></span>
      <span>Recenza media <b>{c.avg_recency_days} gg</b></span><span>Punti medi <b>{num(c.avg_points)}</b></span>
    </div>
    {c.action && <p className="mt-2 rounded-md bg-blue-50 px-2 py-1.5 text-[11px] text-blue-800">{c.action}</p>}
    <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3">
      <Button size="sm" variant="outline" onClick={onMembers} disabled={!c.count} data-testid={`${testId}-members`}><Users className="mr-1.5 h-3.5 w-3.5" />Clienti</Button>
      <Button size="sm" variant="outline" onClick={onCampaign} disabled={!c.count} data-testid={`${testId}-campaign`}><Send className="mr-1.5 h-3.5 w-3.5" />Comunicazione</Button>
    </div>
  </div>
);

const RfmTab = ({ onMembers, onCampaign }) => {
  const [th, setTh] = useState(null);
  const [data, setData] = useState(null);
  const load = () => api.get("/clusters/rfm").then((r) => { setData(r.data); setTh(r.data.thresholds); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.put("/clusters/rfm/config", { r: th.r.map(Number), f: th.f.map(Number), m: th.m.map(Number) }); toast.success("Parametri salvati"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const auto = async () => { try { setTh((await api.post("/clusters/rfm/auto")).data); toast.success("Soglie calcolate sui quintili: salva per applicarle"); } catch (e) { toast.error(errMsg(e)); } };
  if (!th) return <Empty text="Caricamento…" />;
  return (
    <div className="space-y-6">
      <Panel title="Parametri RFM" testId="rfm-params" action={<div className="flex gap-2">
        <Button size="sm" variant="outline" data-testid="rfm-auto-button" onClick={auto}><Wand2 className="mr-1.5 h-3.5 w-3.5" />Soglie automatiche</Button>
        <Button size="sm" data-testid="rfm-save-button" className="bg-blue-600 hover:bg-blue-700" onClick={save}>Salva e ricalcola</Button></div>}>
        <div className="space-y-3 p-5">
          {DIMS.map(([k, l, op]) => (
            <div key={k} className="grid items-center gap-2 md:grid-cols-[260px_1fr]">
              <span className="text-sm text-slate-700">{l}</span>
              <div className="grid grid-cols-4 gap-2">
                {th[k].map((v, i) => (
                  <label key={i} className="text-[10px] text-slate-400">punteggio {k === "r" ? 5 - i : i + 2} se {op}
                    <Input type="number" min="0" className="h-8 text-xs" data-testid={`rfm-${k}-${i}`} value={v} onChange={(e) => setTh({ ...th, [k]: th[k].map((x, j) => (j === i ? e.target.value : x)) })} /></label>
                ))}
              </div>
            </div>
          ))}
          <p className="text-xs text-slate-400">I punti vendita, i giorni e gli orari si impostano dai Filtri in alto: il calcolo considera solo gli acquisti che li rispettano.</p>
        </div>
      </Panel>
      {data && <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.groups.map((g) => <ClusterCard key={g.key} c={g} testId={`rfm-cluster-${g.key}`} onMembers={() => onMembers(`rfm:${g.key}`)} onCampaign={() => onCampaign(`rfm:${g.key}`, `RFM · ${g.name}`)} />)}
      </div>}
    </div>
  );
};

const AutoTab = ({ onMembers, onCampaign }) => {
  const [k, setK] = useState("5");
  const [runs, setRuns] = useState([]);
  const [cur, setCur] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/clusters/auto").then((r) => { setRuns(r.data); setCur((c) => c || r.data[0] || null); });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => { setBusy("run"); try { const { data } = await api.post("/clusters/auto", { k: Number(k) }); setCur(data); load(); toast.success("Raggruppamento calcolato"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  const describe = async () => { setBusy("ai"); try { const { data } = await api.post(`/clusters/auto/${cur.id}/describe`, { model: "gpt-6-luna" }); setCur(data); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  const del = async (id) => { await api.delete(`/clusters/auto/${id}`); if (cur?.id === id) setCur(null); load(); };
  return (
    <div className="space-y-6">
      <Panel title="Raggruppamento automatico (k-means su spesa, frequenza, recenza, punti)" testId="auto-params">
        <div className="flex flex-wrap items-center gap-3 p-5">
          <span className="text-sm text-slate-600">Numero di gruppi</span>
          <Select value={k} onValueChange={setK}><SelectTrigger className="w-24" data-testid="auto-k-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{[2, 3, 4, 5, 6, 7, 8].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent></Select>
          <Button data-testid="auto-run-button" onClick={run} disabled={!!busy} className="bg-blue-600 hover:bg-blue-700">{busy === "run" ? "Calcolo…" : "Calcola gruppi"}</Button>
          {cur && <Button variant="outline" data-testid="auto-describe-button" onClick={describe} disabled={!!busy}><Sparkles className="mr-2 h-4 w-4" />{busy === "ai" ? "L'IA sta descrivendo…" : "Descrivi con IA"}</Button>}
        </div>
        {runs.length > 0 && <div className="flex flex-wrap gap-2 border-t border-slate-100 px-5 py-3">
          {runs.map((r) => (
            <span key={r.id} className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs ${cur?.id === r.id ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}>
              <button data-testid={`auto-run-${r.id}`} onClick={() => setCur(r)}>{fdatetime(r.created_at)} · {r.k} gruppi</button>
              <button onClick={() => del(r.id)} className="text-slate-300 hover:text-rose-600"><Trash2 className="h-3 w-3" /></button>
            </span>
          ))}
        </div>}
      </Panel>
      {cur ? <>
        {cur.description && <p className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900" data-testid="auto-summary">{cur.description}</p>}
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {cur.clusters.map((c) => <ClusterCard key={c.idx} c={c} testId={`auto-cluster-${c.idx}`} onMembers={() => onMembers(`auto:${cur.id}/${c.idx}`)} onCampaign={() => onCampaign(`auto:${cur.id}/${c.idx}`, `Auto · ${c.name}`)} />)}
        </div>
      </> : <Panel><Empty text="Calcola un raggruppamento per vedere i gruppi di clienti." /></Panel>}
    </div>
  );
};

export default function Clusters() {
  const [members, setMembers] = useState(null);
  const nav = useNavigate();
  const showMembers = async (target) => { try { setMembers((await api.get("/clusters/members", { params: { target } })).data); } catch (e) { toast.error(errMsg(e)); } };
  const campaign = (target, name) => nav(`/comunicazioni?target=${encodeURIComponent(target)}&name=${encodeURIComponent(name)}`);
  return (
    <div>
      <PageHeader title="Cluster clienti" subtitle="Individua gruppi di clienti con parametri RFM o in automatico e prepara comunicazioni mirate" />
      <Tabs defaultValue="rfm">
        <TabsList className="mb-4"><TabsTrigger value="rfm" data-testid="clusters-tab-rfm">Parametri RFM</TabsTrigger><TabsTrigger value="auto" data-testid="clusters-tab-auto">Automatico</TabsTrigger></TabsList>
        <TabsContent value="rfm"><RfmTab onMembers={showMembers} onCampaign={campaign} /></TabsContent>
        <TabsContent value="auto"><AutoTab onMembers={showMembers} onCampaign={campaign} /></TabsContent>
      </Tabs>
      <Sheet open={!!members} onOpenChange={(o) => !o && setMembers(null)}>
        <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-md" data-testid="cluster-members-sheet">
          <SheetHeader><SheetTitle className="font-heading">{members?.name} · {num(members?.total)} clienti</SheetTitle></SheetHeader>
          {members && members.total > members.members.length && <p className="mt-1 text-xs text-slate-400">Mostrati i primi {members.members.length} per spesa</p>}
          <div className="mt-4 divide-y divide-slate-100 pr-2">
            {members?.members.map((c) => (
              <button key={c.id} onClick={() => nav(`/clienti/${c.id}`)} className="flex w-full items-center justify-between py-3 text-left hover:bg-slate-50">
                <div><div className="text-sm font-medium">{c.name}</div><div className="text-xs text-slate-400">{eur(c.total_spent)} · {num(c.tx_count)} scontrini</div></div><TierBadge tier={c.tier} />
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
